"use client";

import Link from "next/link";
import Image from "next/image";
import type { ComponentPropsWithoutRef, ReactNode } from "react";
import { useEffect, useState } from "react";
import { usePathname } from "next/navigation";
import { CalendarDays, ChevronDown, ClipboardList, FilePenLine, History, LoaderCircle, LogOut, Mail, Menu, MessageSquareText, RefreshCw, ShieldCheck, UserRound, Users, X } from "lucide-react";
import { siteConfig } from "@/lib/site-config";
import { SocialButtons } from "@/components/social-buttons";
import { ThemeSwitcher } from "@/components/theme-switcher";
import { Button } from "@/components/ui/button";
import { ButtonGroup } from "@/components/ui/button-group";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { DropdownMenu, DropdownMenuGroup, DropdownMenuItem, DropdownMenuLabel, DropdownMenuPortal, DropdownMenuSeparator, DropdownMenuSub, DropdownMenuSubContent, DropdownMenuSubTrigger, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { defaultSiteSettings, type SiteSettings } from "@/lib/site-settings";
import { elenailsTheme } from "@/lib/theme";
import { LiquidGlassMenuContent } from "@/components/liquid-glass-menu";

type SessionUser = { firstName: string; email: string; canAccessAdmin?: boolean; profilePhotoUrl?: string | null };
const themeClasses = elenailsTheme.classes;
const themeMenu = elenailsTheme.menu;
const themeStyles = elenailsTheme.styles;

// Module state intentionally survives App Router segment swaps. It prevents the
// account affordance from falling back to "Espace client" while the next page
// loads and its Header instance requests the same session again.
let cachedSessionUser: SessionUser | null | undefined;
let sessionRequest: Promise<SessionUser | null> | null = null;

async function readSession(force = false) {
  if (!force && cachedSessionUser !== undefined) return cachedSessionUser;
  if (!force && sessionRequest) return sessionRequest;

  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), 4500);

  sessionRequest = fetch("/api/auth/session", { cache: "no-store", credentials: "same-origin", signal: controller.signal })
    .then(async (response) => {
      if (!response.ok) throw new Error("Impossible de lire la session.");
      const data = await response.json();
      const nextUser: SessionUser | null = data.user || null;
      cachedSessionUser = nextUser;
      return nextUser;
    })
    .catch((): SessionUser | null => cachedSessionUser ?? null)
    .finally(() => {
      window.clearTimeout(timeout);
      sessionRequest = null;
    });

  return sessionRequest;
}

function SiteNavigationLink({ href, children, onClick, ...linkProps }: { href: string; children: ReactNode; onClick?: () => void } & Omit<ComponentPropsWithoutRef<"a">, "href" | "children" | "onClick">) {
  if (href.includes("#")) {
    return <a href={href} onClick={onClick} {...linkProps}>{children}</a>;
  }

  return <Link href={href} onClick={onClick} {...linkProps}>{children}</Link>;
}

function userAvatarLabel(name: string) {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() || "")
    .join("") || "U";
}

function AccountAvatar({ name, photoUrl }: { name: string; photoUrl?: string | null }) {
  return (
    <Avatar className="header-account-avatar border border-border" size="default">
      {photoUrl ? <AvatarImage src={photoUrl} alt={name} /> : null}
      <AvatarFallback>{userAvatarLabel(name)}</AvatarFallback>
    </Avatar>
  );
}

function AccountMenuIdentity({ user }: { user: SessionUser }) {
  return (
    <DropdownMenuLabel className={themeClasses.accountIdentity}>
      <AccountAvatar name={user.firstName} photoUrl={user.profilePhotoUrl} />
      <span>
        <strong>{user.firstName}</strong>
        <small>{user.email}</small>
      </span>
    </DropdownMenuLabel>
  );
}

function AdminMenuItems({ onNavigate, onRefresh }: { onNavigate?: () => void; onRefresh: () => void }) {
  const handleRefresh = () => {
    onNavigate?.();
    onRefresh();
  };

  return (
    <>
      <DropdownMenuGroup>
        <DropdownMenuLabel className={themeClasses.accountSectionLabel}>Gestion</DropdownMenuLabel>
        <DropdownMenuItem asChild><Link href="/admin?tab=appointments" onClick={onNavigate}><CalendarDays /> Rendez-vous</Link></DropdownMenuItem>
        <DropdownMenuItem asChild><Link href="/admin?tab=reviews" onClick={onNavigate}><MessageSquareText /> Avis</Link></DropdownMenuItem>
        <DropdownMenuItem asChild><Link href="/admin/users" onClick={onNavigate}><Users /> Utilisateurs</Link></DropdownMenuItem>
        <DropdownMenuItem asChild><Link href="/admin/contact" onClick={onNavigate}><MessageSquareText /> Messages</Link></DropdownMenuItem>
        <DropdownMenuItem asChild><Link href="/admin/emails" onClick={onNavigate}><Mail /> E-mails</Link></DropdownMenuItem>
      </DropdownMenuGroup>
      <DropdownMenuSeparator />
      <DropdownMenuGroup>
        <DropdownMenuLabel className={themeClasses.accountSectionLabel}>Site</DropdownMenuLabel>
        <DropdownMenuItem asChild><Link href="/admin/editor" onClick={onNavigate}><FilePenLine /> Éditeur du site</Link></DropdownMenuItem>
        <DropdownMenuItem onSelect={handleRefresh}><RefreshCw /> Actualiser</DropdownMenuItem>
      </DropdownMenuGroup>
    </>
  );
}

function AdminSubMenu({ mobile = false, onNavigate, onRefresh }: { mobile?: boolean; onNavigate?: () => void; onRefresh: () => void }) {
  return (
    <DropdownMenuSub>
      <DropdownMenuSubTrigger className={mobile ? themeClasses.mobileSubTrigger : themeClasses.accountSubTrigger}>
        <ShieldCheck /> Administration
      </DropdownMenuSubTrigger>
      <DropdownMenuPortal>
        <DropdownMenuSubContent
          className={mobile ? themeClasses.mobileSubMenu : themeClasses.accountSubMenu}
          style={themeStyles.glassVars}
          sideOffset={mobile ? themeMenu.mobileSubSideOffset : themeMenu.accountSubSideOffset}
          alignOffset={mobile ? themeMenu.mobileSubAlignOffset : themeMenu.accountSubAlignOffset}
          collisionPadding={mobile ? themeMenu.mobileCollisionPadding : themeMenu.collisionPadding}
        >
          <AdminMenuItems onNavigate={onNavigate} onRefresh={onRefresh} />
        </DropdownMenuSubContent>
      </DropdownMenuPortal>
    </DropdownMenuSub>
  );
}

export function Header({ admin = false, onAdminRefresh, onAdminLogout }: { admin?: boolean; onAdminRefresh?: () => void; onAdminLogout?: () => void | Promise<void> }) {
  const [open, setOpen] = useState(false);
  const [user, setUser] = useState<SessionUser | null>(() => cachedSessionUser ?? null);
  const [siteSettings, setSiteSettings] = useState<SiteSettings>(defaultSiteSettings());
  const [loggingOut, setLoggingOut] = useState(false);
  const pathname = usePathname();

  useEffect(() => {
    let active = true;
    const loadSession = (force = false) => {
      void readSession(force).then((nextUser) => {
        if (active) setUser(nextUser);
      });
    };
    const timeout = window.setTimeout(loadSession, 0);
    const handleSessionChange = () => loadSession(true);
    window.addEventListener("elenails:session-changed", handleSessionChange);
    return () => {
      active = false;
      window.clearTimeout(timeout);
      window.removeEventListener("elenails:session-changed", handleSessionChange);
    };
  }, []);

  useEffect(() => { void fetch("/api/site-settings", { cache: "no-store" }).then((response) => response.ok ? response.json() : null).then((data) => { if (data?.settings) setSiteSettings(data.settings); }).catch(() => undefined); }, []);

  async function logout() {
    if (loggingOut) return;
    setLoggingOut(true);
    setOpen(false);
    try {
      const response = await fetch("/api/auth/logout", { method: "POST" });
      if (!response.ok) throw new Error("La déconnexion a échoué.");
      cachedSessionUser = null;
      setUser(null);
      window.dispatchEvent(new Event("elenails:session-changed"));
      await onAdminLogout?.();
      window.location.replace(admin ? "/connexion?next=%2Fadmin" : "/");
    } catch {
      setLoggingOut(false);
    }
  }

  function refreshAdmin() {
    if (onAdminRefresh) onAdminRefresh();
    else window.location.reload();
  }

  function handleSiteNavigationClick(href: string) {
    setOpen(false);

    // On the home page, handle section links ourselves so the sticky header
    // cannot leave the browser at the top or hide the section under the bar.
    if (href.startsWith("/#") && window.location.pathname === "/") {
      const target = document.getElementById(href.slice(2));
      if (target) {
        window.setTimeout(() => target.scrollIntoView({ behavior: "smooth", block: "start" }), 0);
        window.history.replaceState(null, "", href);
      }
    }
  }

  return (
    <>
      <header className={themeClasses.header} style={themeStyles.headerGlassVars}>
        <div className="container header-inner">
          <Link href="/" className="brand" aria-label={`${siteSettings.societyName}, accueil`} onClick={() => setOpen(false)}>
            <Image className="brand-image" src="/og/elenails-hair.jpg" alt="" width={40} height={40} />
            <span>{siteSettings.societyName}</span>
          </Link>
          <ButtonGroup className="desktop-nav" aria-label="Navigation principale">
            {siteConfig.nav.map((item) => {
              const basePath = item.href.split("#")[0] || "/";
              const active = item.href === "/" ? pathname === "/" : basePath !== "/" && (pathname === basePath || pathname.startsWith(`${basePath}/`));
              return <SiteNavigationLink className={active ? "is-active" : undefined} key={item.href} href={item.href} onClick={() => handleSiteNavigationClick(item.href)}>{item.label}</SiteNavigationLink>;
            })}
          </ButtonGroup>
          <div className="header-actions">
            <div className={themeClasses.accountActions}>
              {user ? <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button className={themeClasses.accountTrigger} variant="ghost" type="button" aria-label={`Ouvrir le menu de ${user.firstName}`}>
                    <AccountAvatar name={user.firstName} photoUrl={user.profilePhotoUrl} /> <span>{user.firstName}</span><ChevronDown data-icon="inline-end" />
                  </Button>
                </DropdownMenuTrigger>
                  <LiquidGlassMenuContent className={themeClasses.accountMenu} align="end" sideOffset={themeMenu.accountSideOffset} collisionPadding={themeMenu.collisionPadding}>
                  <AccountMenuIdentity user={user} />
                  <DropdownMenuGroup>
                    <DropdownMenuItem asChild><Link href="/espace-client" aria-current={pathname === "/espace-client" ? "page" : undefined}><CalendarDays /> Prochains rendez-vous</Link></DropdownMenuItem>
                    <DropdownMenuItem asChild><Link href="/espace-client?onglet=historique"><History /> Historique</Link></DropdownMenuItem>
                    <DropdownMenuItem asChild><Link href="/espace-client?onglet=avis"><MessageSquareText /> Mes avis</Link></DropdownMenuItem>
                    <DropdownMenuItem asChild><Link href="/espace-client?onglet=profil"><UserRound /> Mon profil</Link></DropdownMenuItem>
                    <DropdownMenuItem asChild><Link href="/mes-demandes" aria-current={pathname === "/mes-demandes" ? "page" : undefined}><ClipboardList /> Voir aussi les demandes anonymes</Link></DropdownMenuItem>
                  </DropdownMenuGroup>
                  {user.canAccessAdmin && <>
                    <DropdownMenuSeparator />
                    <AdminSubMenu onRefresh={refreshAdmin} />
                  </>}
                  <DropdownMenuSeparator />
                  <DropdownMenuGroup><DropdownMenuItem className={themeClasses.accountLogout} disabled={loggingOut} onClick={() => void logout()}>{loggingOut ? <><LoaderCircle className="spin" /> Déconnexion…</> : <><LogOut /> Déconnexion</>}</DropdownMenuItem></DropdownMenuGroup>
                  </LiquidGlassMenuContent>
              </DropdownMenu> : <Link className="header-my-bookings" href="/espace-client"><UserRound /> <span>Espace client</span></Link>}
            </div>
            <ThemeSwitcher />
            <div className="header-socials"><SocialButtons settings={siteSettings} /></div>
            <DropdownMenu
              open={open}
              onOpenChange={setOpen}
            >
              <DropdownMenuTrigger asChild>
                <Button className="mobile-menu-button" variant="ghost" size="icon" type="button" aria-label={open ? "Fermer le menu" : "Ouvrir le menu"}>{open ? <X /> : <Menu />}</Button>
              </DropdownMenuTrigger>
              <LiquidGlassMenuContent className={themeClasses.mobileMenu} align="end" sideOffset={themeMenu.mobileSideOffset} collisionPadding={themeMenu.mobileCollisionPadding}>
                {user ? <AccountMenuIdentity user={user} /> : <DropdownMenuLabel className={themeClasses.mobileMenuLabel}>Navigation</DropdownMenuLabel>}
                {user && <DropdownMenuLabel className={themeClasses.mobileMenuLabel}>Navigation</DropdownMenuLabel>}
                <DropdownMenuGroup>
                  {siteConfig.nav.map((item) => <DropdownMenuItem key={item.href} asChild><SiteNavigationLink href={item.href} onClick={() => handleSiteNavigationClick(item.href)}>{item.label}</SiteNavigationLink></DropdownMenuItem>)}
                    {user && <><DropdownMenuSeparator /><DropdownMenuLabel className={themeClasses.mobileMenuLabel}>Mon espace</DropdownMenuLabel></>}
                    {user ? <>
                      <DropdownMenuItem asChild><Link href="/espace-client" onClick={() => setOpen(false)}><CalendarDays /> Prochains rendez-vous</Link></DropdownMenuItem>
                      <DropdownMenuItem asChild><Link href="/espace-client?onglet=historique" onClick={() => setOpen(false)}><History /> Historique</Link></DropdownMenuItem>
                      <DropdownMenuItem asChild><Link href="/espace-client?onglet=avis" onClick={() => setOpen(false)}><MessageSquareText /> Mes avis</Link></DropdownMenuItem>
                      <DropdownMenuItem asChild><Link href="/espace-client?onglet=profil" onClick={() => setOpen(false)}><UserRound /> Mon profil</Link></DropdownMenuItem>
                    </> : <DropdownMenuItem asChild><Link href="/espace-client" onClick={() => setOpen(false)}><UserRound /> Se connecter ou s’inscrire</Link></DropdownMenuItem>}
                    <DropdownMenuItem asChild><Link href="/mes-demandes" onClick={() => setOpen(false)}><ClipboardList /> Mes demandes sans compte</Link></DropdownMenuItem>
                    {user?.canAccessAdmin && <>
                      <DropdownMenuSeparator />
                      <AdminSubMenu mobile onNavigate={() => setOpen(false)} onRefresh={refreshAdmin} />
                    </>}
                    {user && <DropdownMenuItem className={themeClasses.accountLogout} disabled={loggingOut} onClick={() => void logout()}>{loggingOut ? <><LoaderCircle className="spin" /> Déconnexion…</> : <><LogOut /> Déconnexion</>}</DropdownMenuItem>}
                  
                </DropdownMenuGroup>
                <DropdownMenuSeparator />
                <div className="mobile-menu-socials"><SocialButtons settings={siteSettings} /></div>
              </LiquidGlassMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>
    </>
  );
}
